import { PlayerInterface, SourceTextTrack, TextTrack } from '@foundation-player/types';
import { TextTrackSelectedEvent } from '../event-emitter/unfiltered';
import { VttCueEventEmitter } from './vtt-cue-event-emitter';

export class VttTracksContainer {
  private activeVttCue?: VttCueEventEmitter;
  private textTracks?: SourceTextTrack[];
  private vttStrings: {
    [id: string]: string;
  } = {};

  constructor(readonly player: PlayerInterface) {}

  async loadTextTracks(textTracks: SourceTextTrack[]): Promise<void> {
    this.textTracks = [];
    this.vttStrings = {};

    let vttTextTrackPromises: Promise<{ track: SourceTextTrack; content: string }>[] = [];
    vttTextTrackPromises = textTracks.map(async (track) => {
      const response = await fetch(track.url);

      if (response.status !== 200) {
        return Promise.reject();
      }

      return {
        track,
        content: await response.text(),
      };
    });

    const results = await Promise.allSettled(vttTextTrackPromises);

    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        this.textTracks?.push(result.value.track);
        this.vttStrings[result.value.track.id] = result.value.content;
      }
    });

    return Promise.resolve();
  }

  enableTextTrack(id: string) {
    this.disableTextTrack();

    const textTrack = this.getTextTrackById(id);

    if (!textTrack) {
      return;
    }

    this.activeVttCue = new VttCueEventEmitter(id, this.vttStrings[id], this.player);
    textTrack.isActive = true;
    this.player.dispatchEvent(new TextTrackSelectedEvent({ textTrack }));
  }

  disableTextTrack() {
    if (this.activeVttCue) {
      const textTrack = this.getActiveTextTrack();
      this.activeVttCue.stopEmitting();
      this.activeVttCue = undefined;
      if (textTrack) {
        textTrack.isActive = false;
        this.player.dispatchEvent(new TextTrackSelectedEvent({ textTrack }));
      }
    }
  }

  getTextTracks(): TextTrack[] {
    return this.textTracks || [];
  }

  getActiveTextTrack() {
    if (!this.activeVttCue) {
      return undefined;
    }

    return this.getTextTrackById(this.activeVttCue.vttId) as TextTrack;
  }

  private getTextTrackById(id: string) {
    return this.textTracks?.find((track) => track.id === id);
  }
}
